import { Component, Input, OnChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { ControlForm, Project } from 'src/app/shared/models';
import { ControlFormService } from '../../../../../../../core/control-form.service';

/**
 * Project Settings > Forms.
 *
 * One list, two groups: the forms this project already has, and the forms its
 * organization has on OTHER projects. Selecting either previews it; adding one
 * from the second group copies it into this project.
 *
 * Forms (control_form) are project-scoped in the schema — there are no org-level
 * forms — so "available" forms are really other projects' forms, and adding one
 * clones it. A form that several projects share is listed once, as the copy from
 * the most recently created project.
 */
@Component({
  selector: 'app-forms-list',
  templateUrl: './forms-list.component.html',
  styleUrls: ['./forms-list.component.scss'],
  standalone: false,
})
export class FormsListComponent implements OnChanges {
  // `display_format` is an internal enum key; the list shows what it means
  // instead. Unknown values fall through to the raw key rather than
  // disappearing, so a new format added on the API side still reads.
  private static readonly FORMAT_LABELS = {
    header_lines: 'Header + line items',
    checklist: 'Checklist',
    generic: 'Sections',
  };

  @Input() project: Project;

  public loading = 0;
  public initialized = false;
  public search = '';
  public adding: string = null;

  public projectForms: ControlForm[] = [];
  public availableForms: ControlForm[] = [];
  public form: ControlForm = null;

  // Subject for the "can this user add forms here?" ability check.
  public newForm: ControlForm;

  private loadedProjectId: string = null;

  constructor(
    private controlFormSrv: ControlFormService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  // The settings tab renders this component before the project resolves out of
  // the store, so the fetch hangs off the input rather than ngOnInit — otherwise
  // the first (project-less) pass would leave the list permanently empty.
  ngOnChanges(): void {
    const projectId = this.project?.id;
    if (!projectId || projectId === this.loadedProjectId) {
      return;
    }
    this.loadedProjectId = projectId;
    this.newForm = new ControlForm({ project_id: projectId });
    this.fetchForms();
  }

  get organizationId(): string {
    return this.project?.owner_organization?.id || this.project?.owner_organization_id || null;
  }

  get visibleProjectForms(): ControlForm[] {
    return this.filter(this.projectForms);
  }

  get visibleAvailableForms(): ControlForm[] {
    return this.filter(this.availableForms);
  }

  private filter(forms: ControlForm[]): ControlForm[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return forms;
    }
    return forms.filter(f => `${f.name || ''} ${f.type || ''} ${f.project?.name || ''}`
      .toLowerCase()
      .includes(term));
  }

  fetchForms(selectId: string = null): void {
    if (!this.project?.id) {
      this.initialized = true;
      return;
    }

    this.loading++;
    Promise.all([
      this.controlFormSrv.getProjectForms(this.project.id),
      this.controlFormSrv.getOrganizationForms(this.organizationId, { projectId: this.project.id }),
    ])
      .then(([own, org]: [any[], any[]]) => {
        this.projectForms = own.map(o => new ControlForm(o));
        // The org listing includes this project's own forms; they are already in
        // the first group, and the API rejects a second copy of them anyway.
        this.availableForms = org
          .map(o => new ControlForm(o))
          .filter(o => !o.in_target_project);

        const selected = selectId
          ? this.projectForms.find(o => o.id === selectId)
          : null;
        this.changeSelection(selected || this.projectForms[0] || this.availableForms[0] || null);
      })
      .catch((resp: HttpErrorResponse) => {
        this.snackBar.open(resp.error?.error || 'Could not load forms.', '', { duration: 5000 });
      })
      .finally(() => {
        this.loading--;
        this.initialized = true;
      });
  }

  changeSelection(form: ControlForm): void {
    [...this.projectForms, ...this.availableForms].forEach(o => (o.selected = false));
    this.form = form || null;
    if (this.form) {
      this.form.selected = true;
    }
  }

  /**
   * The shape of the form, in words.
   */
  formatLabel(form: ControlForm): string {
    return FormsListComponent.FORMAT_LABELS[form?.display_format] || form?.display_format || '';
  }

  /**
   * Copies a form from another project into this one, after confirming — the
   * copy lands in the field app for every inspector on the project, so it is
   * not something to trigger by a stray click on a dense list. `$event` is
   * stopped so the row's own click (select + preview) does not also fire.
   */
  add(form: ControlForm, event: Event = null): void {
    event?.stopPropagation();

    if (!form?.id || !this.project?.id || this.adding) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Add form?',
        `<b>${form.name}</b> will be available on this project's daily reports.`,
        'Add form',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.copy(form);
      }
    });
  }

  private copy(form: ControlForm): void {
    this.adding = form.id;
    this.controlFormSrv.assignToProject(this.project.id, form.id)
      .then((assigned: any) => {
        this.snackBar.open(`Added "${assigned?.name || form.name}" to this project`, '', { duration: 5000 });
        this.fetchForms(assigned?.id);
      })
      .catch((resp: HttpErrorResponse) => {
        this.snackBar.open(resp.error?.error || 'Could not add the form.', '', { duration: 5000 });
      })
      .finally(() => {
        this.adding = null;
      });
  }
}
